import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Crown, Zap, Building2, Loader2, Calendar } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const planIcons = {
  free: Zap,
  pro: Crown,
  enterprise: Building2,
  enterprise_annual: Calendar
};

const planColors = {
  free: "border-gray-600",
  pro: "border-green-500 ring-2 ring-green-500/20",
  enterprise: "border-yellow-500",
  enterprise_annual: "border-purple-500 ring-2 ring-purple-500/20"
};

export default function SubscriptionPage({ user, onLogout }) {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchCurrentSubscription();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/plans`);
      const data = await response.json();
      setPlans(data.plans || []);
    } catch (error) {
      // Error handled silently
      toast.error("Error al cargar los planes");
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCurrentSubscription(data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (planId === "free") {
      toast.info("Ya tienes acceso al plan gratuito");
      return;
    }

    setProcessingPlan(planId);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/flow/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Error al procesar el pago");
      }

      const data = await response.json();
      
      // Redirect to Flow.cl payment page
      window.location.href = data.url;
      
    } catch (error) {
      // Error handled silently
      toast.error(error.message || "Error al iniciar el proceso de pago");
      setProcessingPlan(null);
    }
  };

  const formatPrice = (plan) => {
    if (plan.price_clp === 0) return "Gratis";
    const formattedPrice = `$${plan.price_clp.toLocaleString("es-CL")}`;
    return formattedPrice;
  };

  const getBillingPeriod = (plan) => {
    return plan.billing_period === "annual" ? "/año" : "/mes";
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-green-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="subscription-page">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-white">
            Planes de Suscripción
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Elige el plan que mejor se adapte a tus necesidades de gestión de frecuencias RF
          </p>
          <Badge className="bg-blue-900/50 text-blue-400 border-blue-700">
            Pagos seguros con Flow.cl (Webpay, Servipag, Multicaja)
          </Badge>
        </div>

        {/* Current Plan Badge */}
        {currentSubscription && (
          <div className="flex justify-center">
            <Badge className="bg-green-900/50 text-green-400 border-green-700 px-4 py-2">
              Plan actual: {currentSubscription.plan_name}
            </Badge>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const Icon = planIcons[plan.id] || Zap;
            const isCurrentPlan = currentSubscription?.plan === plan.id;
            const isAnnual = plan.billing_period === "annual";
            
            return (
              <Card 
                key={plan.id}
                className={`bg-gray-900/50 ${planColors[plan.id] || "border-gray-600"} relative overflow-hidden`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.id === "pro" && (
                  <div className="absolute top-0 right-0 bg-green-500 text-black text-xs font-bold px-3 py-1">
                    POPULAR
                  </div>
                )}
                {isAnnual && (
                  <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1">
                    AHORRA $149.880
                  </div>
                )}
                
                <CardHeader className="text-center pb-2">
                  <div className={`mx-auto w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${
                    isAnnual ? "bg-purple-900/50" : "bg-gray-800"
                  }`}>
                    <Icon className={`w-6 h-6 ${
                      plan.id === "pro" ? "text-green-400" : 
                      plan.id === "enterprise" ? "text-yellow-400" : 
                      plan.id === "enterprise_annual" ? "text-purple-400" : "text-gray-400"
                    }`} />
                  </div>
                  <CardTitle className="text-xl text-white">{plan.name}</CardTitle>
                  <CardDescription>
                    <span className="text-3xl font-bold text-white">
                      {formatPrice(plan)}
                    </span>
                    {plan.price_clp > 0 && (
                      <span className="text-gray-400 text-sm"> CLP{getBillingPeriod(plan)}</span>
                    )}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                
                <CardFooter>
                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={isCurrentPlan || processingPlan === plan.id}
                    className={`w-full ${
                      plan.id === "pro" 
                        ? "bg-green-600 hover:bg-green-700 text-white" 
                        : plan.id === "enterprise"
                        ? "bg-yellow-600 hover:bg-yellow-700 text-black"
                        : plan.id === "enterprise_annual"
                        ? "bg-purple-600 hover:bg-purple-700 text-white"
                        : "bg-gray-700 hover:bg-gray-600 text-white"
                    }`}
                    data-testid={`subscribe-btn-${plan.id}`}
                  >
                    {processingPlan === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Procesando...
                      </>
                    ) : isCurrentPlan ? (
                      "Plan Actual"
                    ) : plan.id === "free" ? (
                      "Plan Gratuito"
                    ) : (
                      "Pagar con Flow"
                    )}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Features Comparison Table */}
        <Card className="bg-gray-900/50 border-gray-700 mt-8">
          <CardHeader>
            <CardTitle className="text-white">Comparación de Características</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-400">Característica</th>
                    {plans.map(plan => (
                      <th key={plan.id} className="text-center py-3 px-4 text-white">{plan.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4 text-gray-300">Sistemas IEM</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="text-center py-3 px-4 text-white">
                        {plan.limits.max_systems === -1 ? "∞" : plan.limits.max_systems}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4 text-gray-300">Eventos guardados</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="text-center py-3 px-4 text-white">
                        {plan.limits.max_events === -1 ? "∞" : plan.limits.max_events}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4 text-gray-300">Conexión Hardware USB</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="text-center py-3 px-4">
                        {plan.limits.hardware_access ? (
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4 text-gray-300">Reportes PDF</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="text-center py-3 px-4">
                        {plan.limits.pdf_reports ? (
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4 text-gray-300">Historial de escaneos</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="text-center py-3 px-4 text-white">
                        {plan.limits.scan_history_days} días
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 px-4 text-gray-300">Usuarios por cuenta</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="text-center py-3 px-4 text-white">
                        {plan.limits.max_users}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-300">Tipo de soporte</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="text-center py-3 px-4 text-white capitalize">
                        {plan.limits.support_type === "community" ? "Comunidad" :
                         plan.limits.support_type === "email" ? "Email" : "Prioritario"}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Payment Info */}
        <div className="text-center text-gray-500 text-sm">
          <p>Pagos procesados de forma segura por Flow.cl</p>
          <p className="mt-1">Acepta Webpay, tarjetas de crédito/débito, Servipag y Multicaja</p>
        </div>
      </div>
    </Layout>
  );
}
